import type { TranslationFunction } from "@src/packages/i18n";
import type { PostProcedureOutcomeContentKey } from "@src/packages/cycles/presenters/postProcedureOutcome";
// From its own module, never the package index: this file is deliberately free
// of anything that reaches `react-native`, so the script test runner can load
// it — and the index pulls in the follow-up hook.
import { EARLY_PREGNANCY_MILESTONES_REVIEWED } from "../pregnancy/earlyPregnancyMilestones";
import type {
  WhatToKnowBullet,
  WhatToKnowCard,
} from "./components/HomepageReviewConceptHome";
import type { OrganicMiniIconKind } from "./components/OrganicMiniIcon";

/**
 * Kept out of the Home component so the cards can be built in a test.
 *
 * The component reaches `react-native` and cannot be loaded by the script test
 * runner, which meant every key this copy asks for was unverified: a rename or a
 * missing modality left `t()` returning the key itself, and the card rendered
 * `...first_weeks_confirm_the_result_oi` as its body. Building the real cards in a
 * test is what catches that — a hand-copied list of keys would drift the moment
 * this function changed.
 */
export const EARLY_PREGNANCY =
  "homepage.review_concept.post_procedure_outcome.early_pregnancy";

/**
 * The dating this card can put on its steps, when there is one.
 *
 * `windows` is keyed by milestone id and holds a formatted calendar window;
 * `ageLabel` says how far along she is. Both absent is the ordinary case for
 * anyone who has not told us her weeks, and the card falls back to the copy it
 * has always used — true, and vague.
 */
export type EarlyPregnancyDating = {
  ageLabel: string | null;
  windows: Record<string, string>;
  /** The caveat the clinical review's scope requires. */
  indicativeNote: string | null;
};

/**
 * What is worth knowing in the first weeks after a positive result.
 *
 * Deliberately about coordination, not development: what usually comes next,
 * what a beta value can and cannot tell them, and when not to wait for the next
 * appointment. Nothing here interprets their result or their symptoms.
 *
 * The reach-out card names examples and says so. A short list of symptoms under
 * a "call your clinic" heading reads as *the* list of reasons to call, and
 * someone whose symptom is not on it waits. Naming more symptoms is not the fix
 * — thresholds and warning-sign specifics belong to clinically reviewed
 * content, not to a Home card — so the card states in its own body that the
 * list is not complete and that doubt is reason enough, and defers everything
 * else to the clinic. Nothing here claims a symptom is or is not serious.
 *
 * This copy addresses the person who carried the cycle. Callers must not render
 * it to a supporting viewer; `shouldShowEarlyPregnancyGuidance` owns that.
 */
export function buildEarlyPregnancyWhatToKnowCards(
  t: TranslationFunction,
  modalityKey: PostProcedureOutcomeContentKey,
  dating?: EarlyPregnancyDating,
): WhatToKnowCard[] {
  const EP = EARLY_PREGNANCY;

  /**
   * A step with its dates when we can date it, and the wording it has always
   * had when we cannot.
   *
   * Dated and undated steps sit in the same list on purpose. Splitting them into
   * two cards is what produced two "what comes next" headings, and a patient
   * reading her own page does not have two questions about what comes next.
   */
  /**
   * One of the app's own icons per step, so the card is drawn in Louise's hand
   * rather than the platform's.
   *
   * Each names the kind of thing the step is and nothing more: a hormone test, a
   * scan, being handed on, a screening. Nothing here is cheerful — this card is
   * read by someone who has just had a positive result, and a celebratory set
   * would be the app having a feeling about her pregnancy.
   */
  const STEP_ICONS: Record<string, OrganicMiniIconKind> = {
    repeat_beta: "hormones",
    first_scan: "afc",
    care_handover: "concierge",
    first_trimester_screening: "genetics",
  };

  const step = (id: string, undatedKey: string): WhatToKnowBullet | string => {
    const window = dating?.windows[id];
    // The window is hers, worked out from how far along she is, so it is carried
    // as a tag rather than folded into the sentence — a date that reads as part
    // of the copy reads as true of everyone.
    return window
      ? { label: t(`${EP}.milestone_${id}`), tag: window, icon: STEP_ICONS[id] }
      : t(undatedKey);
  };

  const bullets = [
    step("repeat_beta", `${EP}.next_weeks_repeat_beta`),
    step("first_scan", `${EP}.next_weeks_first_scan`),
    step("care_handover", `${EP}.next_weeks_care_handover`),
    // It used to appear only when it could be dated, so a patient who had given
    // no week saw the list stop one step short — the screening simply absent
    // rather than undated. It now has the same undated wording its siblings have:
    // named, hedged to the clinic, and no date claimed.
    //
    // Two conditions, both of them the point. The claim is the milestones'
    // content, so it answers to the switch that publishes them: with
    // `EARLY_PREGNANCY_MILESTONES_REVIEWED` off, the dated rows disappear and this
    // sentence has to disappear with them, or turning the switch off would retract
    // everything except the one line that escaped it.
    //
    // And the undated wording is a fallback for *not knowing*, never for knowing
    // it is behind her: `buildEarlyPregnancyDating` drops a window that has
    // passed, so at sixteen weeks the vague form would announce as upcoming
    // something she has already had, in a list whose siblings carry real dates.
    ...(EARLY_PREGNANCY_MILESTONES_REVIEWED &&
    (!dating || dating.windows.first_trimester_screening)
      ? [
          step(
            "first_trimester_screening",
            `${EP}.next_weeks_first_trimester_screening`,
          ),
        ]
      : []),
    // Not a step and never dated: it is the reassurance that her prescription is
    // the clinic's call, and it belongs after the things that do have dates. It
    // keeps a glyph so the list does not end on a lone bare dot, but a quieter
    // one — it is not somewhere she has to be.
    dating
      ? {
          label: t(`${EP}.clinic_says_what_to_keep_taking`),
          icon: "clinics" as const,
        }
      : t(`${EP}.clinic_says_what_to_keep_taking`),
  ];

  return [
    {
      badge: t(`${EP}.whats_next`),
      title: t(`${EP}.what_usually_comes_next`),
      body: [
        dating?.ageLabel,
        t(`${EP}.first_weeks_confirm_the_result_${modalityKey}`),
        dating?.indicativeNote,
      ]
        .filter((part): part is string => !!part)
        .join(" "),
      bullets,
      tone: "steady",
      // The sequence, not one of its steps. `afc` reads better for a card about
      // the first scan, but the scan is also a bullet inside this card and wears
      // that glyph there — the badge row then repeated an icon sitting two lines
      // below it. This one says "a series of steps", which is what the card is.
      illustration: "process",
    },
    {
      badge: t(`${EP}.about_the_numbers`),
      title: t(`${EP}.one_number_says_little`),
      body: t(`${EP}.a_beta_is_read_with_its_timing`),
      bullets: [
        t(`${EP}.numbers_timing_matters`),
        t(`${EP}.numbers_clinic_reads_trend`),
        t(`${EP}.numbers_louise_keeps_record`),
      ],
      tone: "steady",
      // A hormone test, which is what a beta is.
      illustration: "hormones",
    },
    {
      badge: t(`${EP}.if_something_worries_you`),
      title: t(`${EP}.do_not_wait_to_call`),
      body: t(`${EP}.some_things_are_worth_a_call_today`),
      bullets: [
        t(`${EP}.bleeding_pain_fever_or_anything_else`),
        // Hedged deliberately: whether a clinic takes questions without an
        // appointment is that clinic's policy, not something this product can
        // promise on their behalf.
        t(`${EP}.most_clinics_answer_without_an_appointment`),
        t(`${EP}.reach_out_local_emergency`),
      ],
      tone: "caution",
      // Reaching a clinic, not a symptom: nothing here illustrates a body.
      illustration: "clinics",
    },
  ];
}
